import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UnsubscribeService } from '@app/infrastructure/unsubscribe/unsubscribe.service';
import {
	DEFAULT_PS_MODAL_WIDTH,
	PS_MODAL_ANIMATION_DURATION,
} from '@app/modules/potential-save-modal/potential-savings-modal.config';
import { PotentialSavingsViews } from '@app/modules/potential-save-modal/potential-savings-views.enum';
import { UserService } from '@app/services/user/user.service';
import { ConfigFacadeService } from '@core/services/config/config-facade.service';
import { IPotentialSavingsSteadyStateDTO } from '@modules/potential-save-modal/dto/i-potential-savings-steady-state.dto';
import { IPotentialSavingsDTO } from '@modules/potential-save-modal/dto/i-potential-savings.dto';
import { PotentialSavingsCloseActions } from '@modules/potential-save-modal/potential-savings-close-actions.enum';
import { PotentialSavingsService } from '@modules/potential-save-modal/potential-savings.service';
import { isZestyCoDomain } from '@utils/is-zesty-domain';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeatureFlag } from '@app/infrastructure/feature-flag-manager/feature-flag-decorator';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';

@Component({
	selector: 'app-potential-save-modal',
	templateUrl: './potential-save-modal.component.html',
	styleUrls: ['./potential-save-modal.component.scss'],
	animations: [
		trigger('breakdownExpand', [
			state('collapsed', style({ left: '50%', transform: 'translateX(-50%)' })),
			state('expanded', style({ left: '50%', transform: 'translateX(-500px)' })),
			transition('collapsed <=> expanded', animate(`${PS_MODAL_ANIMATION_DURATION}ms ease`)),
		]),
		trigger('slideInOut', [
			transition(':enter', [
				style({ transform: 'translateX(-100%)' }),
				animate(`${PS_MODAL_ANIMATION_DURATION}ms ease-in`, style({ transform: 'translateY(0%)' })),
			]),
			transition(':leave', [
				animate(`${PS_MODAL_ANIMATION_DURATION}ms ease-out`, style({ transform: 'translateX(-100%)' })),
			]),
		]),
		trigger('fadeInOut', [
			state('in', style({ opacity: 1 })),
			transition(':enter', [
				style({ opacity: 0 }),
				animate(`${PS_MODAL_ANIMATION_DURATION}ms ease-in`, style({ opacity: 1 })),
			]),
			transition(':leave', [animate(`${PS_MODAL_ANIMATION_DURATION}ms ease-out`, style({ opacity: 0 }))]),
		]),
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [UnsubscribeService],
})
export class PotentialSaveModalComponent implements OnInit {
	@FeatureFlag(FEATURE_FLAGS.ENABLE_NEW_ONBOARDING) enableNewOnboarding: boolean;
	public readonly isExtended$: Observable<boolean> = this.potentialSavingsService.isExtended$;
	public readonly isAnimationDisabled$: Observable<boolean> = this.potentialSavingsService.isAnimationDisabled$;
	public readonly isZestyDomain: boolean = isZestyCoDomain();
	public readonly isZestyEmployee: boolean = this.userService.isZestyEmployee();
	public readonly DEFAULT_PS_MODAL_WIDTH = DEFAULT_PS_MODAL_WIDTH;

	public readonly potentialSavingsViews: typeof PotentialSavingsViews = PotentialSavingsViews;
	public readonly currentView$: Observable<PotentialSavingsViews> = this.potentialSavingsService.currentView$;
	public readonly isMonthlyState$: Observable<boolean> = this.potentialSavingsService.isMonthlyState$;
	public readonly potentialSavings$: Observable<IPotentialSavingsDTO> = this.potentialSavingsService.potentialSavings$;
	public readonly isEDP$: Observable<boolean> = this.potentialSavingsService.isEDP$;
	public readonly linuxSteadyState$: Observable<IPotentialSavingsSteadyStateDTO> =
		this.potentialSavingsService.linuxSteadyState$;
	public readonly isAllocationsAndLinuxTabsAllowed$: Observable<boolean> =
		this.potentialSavingsService.isAllocationsAndLinuxTabsAllowed();

	public get productLabel(): string {
		return this.appConfig.config.partner.name;
	}

	constructor(
		public featureFlagManagerService: FeatureFlagManagerService,
		public dialogRef: MatDialogRef<PotentialSaveModalComponent, PotentialSavingsCloseActions>,
		private potentialSavingsService: PotentialSavingsService,
		private appConfig: ConfigFacadeService,
		private router: Router,
		private userService: UserService,
		private unsubscribe$: UnsubscribeService,
	) {}

	public ngOnInit(): void {
		this.potentialSavingsService.loadPotentialSavings().pipe(takeUntil(this.unsubscribe$)).subscribe();
	}

	public expandDialog(): void {
		this.potentialSavingsService.expandDialog();
	}

	public shrinkDialog(): void {
		this.potentialSavingsService.shrinkDialog();
	}

	public onApplyButtonClicked(hasMaterPayer: boolean): void {
		if (this.isZestyDomain && hasMaterPayer) {
			this.dialogRef.close();
			return;
		}
		const path = this.enableNewOnboarding
			? AppRouteHelper.onboardAccount
			: `${AppRouteHelper.onboarding}/${AppRouteHelper.aws}`;
		this.router.navigate([`/${path}`])?.then(() => this.dialogRef.close());
	}

	public toggleIsMonthlyState(isMonthly: boolean): void {
		this.potentialSavingsService.setIsMonthlyState(isMonthly);
	}

	public replaceModalView(view: PotentialSavingsViews): void {
		this.potentialSavingsService.replaceModalView(view);
	}

	public openTotalSavingPanel(): void {
		this.potentialSavingsService.openTotalSavingPanel();
	}

	public refreshData(): void {
		this.dialogRef.close(PotentialSavingsCloseActions.Refresh);
	}

	public openAwsCostExplorer(): void {
		this.potentialSavingsService.openCostExplorerAWS();
	}

	public onToggleEDP($event: boolean): void {
		this.potentialSavingsService.toggleEDP($event).pipe(takeUntil(this.unsubscribe$)).subscribe();
	}

	public onSwitchToEdpClose($event: boolean): void {
		this.potentialSavingsService
			.confirmToggleEDP($event)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => this.potentialSavingsService.closeModal());
	}
}
